import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  NavLink,
  Outlet,
  useParams,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Shield,
  Settings,
  ChevronLeft,
  Megaphone,
  Menu,
  Crown,
  LogOut,
  CalendarDays,
  ChevronRight,
  Building2,
  Plus,
} from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";

function getStorageUrl(bucket: string, path: string) {
  if (!path) return "";
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

/** ===== Types ===== */
type ClubRole =
  | "dirigeant"
  | "cadre_technique"
  | "commission"
  | "coach"
  | "joueur"
  | "lecteur"
  | "parent";

type Club = {
  id: string;
  name: string;
  city: string | null;
  country: string | null;
  logo_path: string | null;
};

type ClubMemberRow = {
  id: string;
  club_id: string;
  user_id: string | null;
  email: string | null;
  role: string;
  status: string;
  accepted_at: string | null;
  created_at: string | null;
};

async function handleLogout() {
  await supabase.auth.signOut();
  window.location.href = "/";
}

/** ===== Helpers ===== */
function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function hasAnyRole(myRoles: ClubRole[], allowed: ClubRole[]) {
  return allowed.some((r) => myRoles.includes(r));
}

function memberRoleToUiRole(dbRole: string): ClubRole | null {
  switch (dbRole) {
    case "dirigeant":
    case "admin":
    case "manager":
      return "dirigeant";

    case "cadre_technique":
    case "assistant":
      return "cadre_technique";

    case "commission":
      return "commission";

    case "coach":
      return "coach";

    case "joueur":
    case "player":
      return "joueur";

    case "lecteur":
    case "viewer":
      return "lecteur";

    case "parent":
    case "player_parent":
      return "parent";

    default:
      return null;
  }
}

function roleLabel(role: ClubRole) {
  switch (role) {
    case "dirigeant":
      return "Dirigeant";
    case "cadre_technique":
      return "Cadre technique";
    case "commission":
      return "Commission";
    case "coach":
      return "Coach";
    case "joueur":
      return "Joueur";
    case "lecteur":
      return "Lecteur";
    case "parent":
      return "Parent";
    default:
      return role;
  }
}

type NavItem = {
  label: string;
  to: string;
  icon: React.ReactNode;
  allowRoles?: ClubRole[] | null;
};

type MyTeam = {
  id: string;
  name: string;
  role: string;
};

export default function ClubLayout() {
  const { clubId } = useParams<{ clubId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [club, setClub] = useState<Club | null>(null);
  const [myRoles, setMyRoles] = useState<ClubRole[]>([]);
  const [myTeams, setMyTeams] = useState<MyTeam[]>([]);
  const [myClubs, setMyClubs] = useState<Club[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadClub() {
      if (!clubId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMsg(null);

        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError) throw authError;
        if (!user) throw new Error("Tu dois être connecté pour accéder à cet espace.");

        const [clubRes, membershipRes, teamMembersRes, myMembershipsRes] = await Promise.all([
          supabase
            .from("clubs")
            .select("id, name, city, country, logo_path")
            .eq("id", clubId)
            .maybeSingle(),
          supabase
            .from("club_members")
            .select("id, club_id, user_id, email, role, status, accepted_at, created_at")
            .eq("club_id", clubId)
            .eq("user_id", user.id)
            .eq("status", "active"),
          supabase
            .from("team_members")
            .select("team_id, role, teams(id, team_name, club_id, archived)")
            .eq("user_id", user.id),
          supabase
            .from("club_members")
            .select("club_id")
            .eq("user_id", user.id)
            .eq("status", "active"),
        ]);

        if (clubRes.error) throw clubRes.error;
        if (membershipRes.error) throw membershipRes.error;
        if (!clubRes.data) throw new Error("Club introuvable.");

        if (myMembershipsRes.error) throw myMembershipsRes.error;
        const otherClubIds = Array.from(
          new Set((myMembershipsRes.data ?? []).map((row) => row.club_id))
        ).filter((id) => id !== clubId);

        let otherClubs: Club[] = [];
        if (otherClubIds.length > 0) {
          const { data: clubsData, error: clubsError } = await supabase
            .from("clubs")
            .select("id, name, city, country, logo_path")
            .in("id", otherClubIds);
          if (clubsError) throw clubsError;
          otherClubs = (clubsData ?? []) as Club[];
        }

        const rows = (membershipRes.data ?? []) as ClubMemberRow[];
        if (rows.length === 0) {
          throw new Error("Tu n’as pas accès à ce club.");
        }

        const roles = rows
          .map((row) => memberRoleToUiRole(row.role))
          .filter(Boolean) as ClubRole[];

        const fetchedTeams: MyTeam[] = ((teamMembersRes.data ?? []) as any[])
          .filter((m) => m.teams?.club_id === clubId && !m.teams?.archived)
          .map((m) => ({
            id: m.teams.id,
            name: m.teams.team_name?.trim() || "Équipe",
            role: m.role ?? "",
          }))
          .sort((a, b) => a.name.localeCompare(b.name, "fr"));

        if (!cancelled) {
          setClub(clubRes.data as Club);
          setMyRoles(Array.from(new Set(roles)));
          setMyTeams(fetchedTeams);
          setMyClubs(
            [clubRes.data as Club, ...otherClubs].sort((a, b) =>
              a.name.localeCompare(b.name, "fr")
            )
          );
        }
      } catch (err: any) {
        if (!cancelled) {
          setErrorMsg(err?.message || "Impossible de charger l’espace club.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadClub();

    return () => {
      cancelled = true;
    };
  }, [clubId]);

  const navItems: NavItem[] = useMemo(() => {
    const base = clubId ? `/club/${clubId}` : "/club/unknown";

    return [
      {
        label: "Dashboard",
        to: `${base}/dashboard`,
        icon: <LayoutDashboard className="h-4 w-4" />,
        allowRoles: null,
      },
      {
        label: "Publications",
        to: `${base}/posts`,
        icon: <Megaphone className="h-4 w-4" />,
        allowRoles: null,
      },
      {
        label: "Mon équipe",
        to: `${base}/my-teams`,
        icon: <Shield className="h-4 w-4" />,
        allowRoles: ["joueur", "parent"],
      },
      {
        label: "Équipes",
        to: `${base}/teams`,
        icon: <Shield className="h-4 w-4" />,
        allowRoles: ["dirigeant", "cadre_technique", "coach"],
      },
      {
        label: "Créer une équipe",
        to: `${base}/teams/new`,
        icon: <Plus className="h-4 w-4" />,
        allowRoles: ["dirigeant", "cadre_technique"],
      },
      {
        label: "Matchs",
        to: `${base}/matches`,
        icon: <LayoutDashboard className="h-4 w-4" />,
        allowRoles: ["dirigeant", "cadre_technique", "coach"],
      },
      {
        label: "Entraînements",
        to: `${base}/trainings`,
        icon: <CalendarDays className="h-4 w-4" />,
        allowRoles: ["dirigeant", "cadre_technique", "coach"],
      },
      {
        label: "Membres",
        to: `${base}/members`,
        icon: <Users className="h-4 w-4" />,
        allowRoles: ["dirigeant", "cadre_technique", "joueur", "parent"],
      },
      {
        label: "Paramètres",
        to: `${base}/settings`,
        icon: <Settings className="h-4 w-4" />,
        allowRoles: ["dirigeant"],
      },
    ];
  }, [clubId]);

  const isTeamRoute = location.pathname.includes(`/club/${clubId}/team/`);

  const filteredNav = useMemo(() => {
    return navItems.filter((it) => {
      if (!it.allowRoles) return true;
      return hasAnyRole(myRoles, it.allowRoles);
    });
  }, [navItems, myRoles]);

  const currentTitle = useMemo(() => {
    const matches = filteredNav.filter((it) => location.pathname.startsWith(it.to));
    const found = matches.sort((a, b) => b.to.length - a.to.length)[0];
    return found?.label ?? "Espace Club";
  }, [filteredNav, location.pathname]);

  const isCoachAppAllowed =
    myRoles.includes("coach") ||
    myRoles.includes("cadre_technique") ||
    myRoles.includes("dirigeant");

  const isPremium = true;

  const logoSrc = club?.logo_path ? getStorageUrl("club-logos", club.logo_path) : "";

  if (!clubId) return <Navigate to="/" replace />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-gray-500 dark:text-gray-400">Chargement…</div>
      </div>
    );
  }

  if (!club || errorMsg) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {errorMsg || "Club introuvable."}
        </div>
      </div>
    );
  }

  const Sidebar = (
    <aside className="h-full w-72 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col">
      <div className="p-4 pt-safe-top pl-safe-left border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="hidden md:inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            onClick={() => window.history.back()}
            aria-label="Retour"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {logoSrc ? (
            <img
              src={logoSrc}
              alt={club.name}
              className="h-9 w-9 rounded-lg object-cover border border-gray-200 dark:border-gray-800 shrink-0"
            />
          ) : (
            <div className="h-9 w-9 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
              <Building2 className="h-4 w-4 text-gray-400" />
            </div>
          )}

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                {club.name}
              </div>
              {isPremium && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200">
                  <Crown className="h-3.5 w-3.5" />
                  Premium
                </span>
              )}
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              Rôles : {myRoles.length ? myRoles.map(roleLabel).join(", ") : "—"}
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {[club.city, club.country].filter(Boolean).join(", ") || "Club"}
            </div>
          </div>
        </div>

        {myClubs.length > 1 && (
          <div className="mt-3">
            <label className="sr-only" htmlFor="club-switcher">
              Changer de club
            </label>
            <select
              id="club-switcher"
              value={clubId}
              onChange={(e) => {
                setMobileOpen(false);
                navigate(`/club/${e.target.value}/dashboard`);
              }}
              className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
            >
              {myClubs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <nav className="p-3 space-y-1 flex-1 overflow-auto">
        {filteredNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cx(
                "flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition",
                "hover:bg-gray-50 dark:hover:bg-gray-800",
                isActive
                  ? "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                  : "text-gray-700 dark:text-gray-200"
              )
            }
          >
            <span className="text-gray-600 dark:text-gray-300">{item.icon}</span>
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}

        {myTeams.length > 0 && (
          <div className="pt-3">
            <div className="px-3 pb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Mes équipes
            </div>
            {myTeams.map((team) => (
              <NavLink
                key={team.id}
                to={`/club/${clubId}/team/${team.id}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cx(
                    "flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition",
                    "hover:bg-gray-50 dark:hover:bg-gray-800",
                    isActive
                      ? "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                      : "text-gray-700 dark:text-gray-200"
                  )
                }
              >
                <span className="text-gray-600 dark:text-gray-300">
                  <Shield className="h-4 w-4" />
                </span>
                <span className="truncate flex-1">{team.name}</span>
                <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      <div className="p-3 border-t border-gray-200 dark:border-gray-800 space-y-3">
        {isCoachAppAllowed && (
          <button
            type="button"
            onClick={() => {
              setMobileOpen(false);
              navigate("/Dashboard");
            }}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800
                       bg-gray-900 text-white hover:bg-black
                       dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100
                       text-sm transition"
            title="Accéder à l’app Coach"
          >
            <LayoutDashboard className="h-4 w-4" />
            App Coach
          </button>
        )}

        <div className="text-xs text-gray-500 dark:text-gray-400">
          Club ID : <span className="font-mono">{club.id}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">Thème</div>
          <ThemeToggle />
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 text-red-700 dark:bg-red-900/10 dark:text-red-200 text-sm hover:bg-red-100 dark:hover:bg-red-900/20 transition"
        >
          <LogOut className="h-4 w-4" />
          Déconnexion
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {!isTeamRoute && (
        <div className="md:hidden sticky top-0 z-30 bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-800 pt-safe-top pl-safe-left pr-safe-right">
          <div className="flex items-center justify-between p-3">
            <button
              type="button"
              className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-gray-200 dark:border-gray-800 text-gray-800 dark:text-gray-100"
              onClick={() => setMobileOpen(true)}
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 px-2 flex items-center gap-2">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={club.name}
                  className="h-7 w-7 rounded-md object-cover border border-gray-200 dark:border-gray-800 shrink-0"
                />
              ) : (
                <div className="h-7 w-7 rounded-md border border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                  <Building2 className="h-3.5 w-3.5 text-gray-400" />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {club.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {currentTitle}
                </div>
              </div>
            </div>
            <div className="w-10" />
          </div>
        </div>
      )}

      {mobileOpen && !isTeamRoute && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72">{Sidebar}</div>
        </div>
      )}

      <div className="hidden md:flex min-h-screen">
        {!isTeamRoute && <div className="sticky top-0 h-screen">{Sidebar}</div>}

        <main className="flex-1">
          {!isTeamRoute && (
            <div className="sticky top-0 z-20 bg-gray-50/80 dark:bg-gray-950/80 backdrop-blur border-b border-gray-200 dark:border-gray-800">
              <div className="px-6 py-4 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {currentTitle}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    Espace Club • {club.name}
                  </div>
                </div>
                {isPremium && (
                  <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200">
                    <Crown className="h-4 w-4" />
                    Premium actif
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={isTeamRoute ? "" : "p-4 md:p-6"}>
            <Outlet />
          </div>
        </main>
      </div>

      <div className={cx("md:hidden", !isTeamRoute && "p-4 pb-safe-bottom pl-safe-left pr-safe-right")}>
        <Outlet />
      </div>
    </div>
  );
}